<!-- @format -->

<template>
	<div
		class="core-currency-percentage-input"
		:class="{ 'core-currency-percentage-input--full-width': fullWidth }"
		:id="dynamicId"
	>
		<div
			v-if="displayOnly || (isTableInput && !isEditing)"
			class="core-currency-percentage-input__row"
		>
			<template
				v-for="(side, index) in orderedSides"
				:key="side"
			>
				<CoreIcon
					v-if="index === 1"
					class="core-currency-percentage-input__swap-icon"
					name="swap-horizontal"
					size="18"
				/>
				<span
					class="p-inputtext p-component core-currency-percentage-input__display-box"
					:class="[
						`core-currency-percentage-input__${side}`,
						{
							'core-currency-percentage-input__display-box--disabled':
								isSideDisabled(side),
						},
					]"
					role="button"
					:tabindex="displayOnly || isFullyDisabled ? -1 : 0"
					@click="openEditor(side)"
					@keydown.enter="openEditor(side)"
				>
					{{ side === 'currency' ? displayCurrencyValue : displayPercentageValue }}
				</span>
				<CoreIcon
					v-if="side === 'percentage' && isPercentageOverMax"
					class="core-currency-percentage-input__warning-icon"
					name="alert-circle-outline"
					size="16"
					:title="$t('§ui.residual_value_exceeds_base')"
				/>
			</template>
		</div>

		<div
			v-else
			class="core-currency-percentage-input__row"
			@keydown.enter="handleEnter"
			@keydown.esc="handleEscape"
			@keydown.tab="handleTab"
		>
			<template
				v-for="(side, index) in orderedSides"
				:key="side"
			>
				<CoreIcon
					v-if="index === 1"
					class="core-currency-percentage-input__swap-icon"
					name="swap-horizontal"
					size="18"
				/>
				<CoreCurrencyInput
					v-if="side === 'currency'"
					ref="currencyInput"
					class="core-currency-percentage-input__currency"
					v-model="currencyValue"
					:currency="currency"
					:maxFractionDigits="currencyDigits"
					:showBorder="showBorder"
					:condensed="condensed"
					:disabled="isCurrencyDisabled"
					:name="currencyName"
					@input="onSideInput('currency', $event)"
					@focus="onSideFocus('currency')"
					@blur="onSideBlur('currency')"
				/>
				<CorePercentageInput
					v-else
					ref="percentageInput"
					class="core-currency-percentage-input__percentage"
					v-model="percentageValue"
					:min="minPercent"
					:maxFractionDigits="percentageDigits"
					:minFractionDigits="0"
					:step="step"
					:showButtons="showButtons"
					:inline="true"
					:condensed="condensed"
					:disabled="isPercentageDisabled"
					:name="percentageName"
					@input="onSideInput('percentage', $event)"
					@focus="onSideFocus('percentage')"
					@blur="onSideBlur('percentage')"
				/>
				<CoreIcon
					v-if="side === 'percentage' && isPercentageOverMax"
					class="core-currency-percentage-input__warning-icon"
					name="alert-circle-outline"
					size="16"
					:title="$t('§ui.residual_value_exceeds_base')"
				/>
			</template>
		</div>

		<CoreInputNotification
			v-if="isFocused"
			:validator="validator"
			:customNotificationMessage="customNotificationMessage"
		/>
	</div>
</template>

<script>
import { useUserStore } from '@/stores/user'
import { roundHalfUp } from '@/helpers/roundHalfUp'
import CoreCurrencyInput from './CoreCurrencyInput.vue'
import CorePercentageInput from './CorePercentageInput.vue'
import CoreInputNotification from './CoreInputNotification.vue'
import CoreIcon from './CoreIcon.vue'
import _ from 'lodash'

export default {
	name: 'CoreCurrencyPercentageInput',

	components: {
		CoreCurrencyInput,
		CorePercentageInput,
		CoreInputNotification,
		CoreIcon,
	},

	props: {
		modelValue: {
			type: [String, Number],
			default: null,
		},
		baseValue: {
			type: Number,
			required: true,
		},
		order: {
			type: String,
			default: 'currency-first',
			validator: (value) => ['currency-first', 'percentage-first'].includes(value),
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		disabledCurrency: {
			type: Boolean,
			default: false,
		},
		disabledPercentage: {
			type: Boolean,
			default: false,
		},
		isTableInput: {
			type: Boolean,
			default: false,
		},
		displayOnly: {
			type: Boolean,
			default: false,
		},
		autofocus: {
			type: Boolean,
			default: false,
		},
		autofocusSide: {
			type: String,
			default: null,
			validator: (value) => [null, 'currency', 'percentage'].includes(value),
		},
		currency: {
			type: String,
			default: null,
		},
		currencyDigits: {
			type: Number,
			default: 0,
		},
		percentageDigits: {
			type: Number,
			default: 0,
		},
		minPercent: {
			type: Number,
			default: 0,
		},
		maxPercent: {
			type: Number,
			default: 100,
		},
		step: {
			type: Number,
			default: 1,
		},
		showButtons: {
			type: Boolean,
			default: false,
		},
		showBorder: {
			type: Boolean,
			default: true,
		},
		fullWidth: {
			type: Boolean,
			default: false,
		},
		condensed: {
			type: Boolean,
			default: false,
		},
		name: {
			type: String,
			default: '',
		},
		validator: {
			type: Object,
			default: null,
		},
		customNotificationMessage: {
			type: Object,
			default: null,
		},
	},

	emits: ['update:modelValue', 'update:percentage', 'focus', 'blur'],

	data() {
		return {
			draftCurrency: null,
			draftPercentage: null,
			typingSide: null,
			typingHeldValue: null,
			focusedSide: null,
			isEditing: false,
			userStore: useUserStore(),
			dynamicId: _.uniqueId('core-currency-percentage-input-'),
		}
	},

	computed: {
		orderedSides() {
			return this.order === 'percentage-first'
				? ['percentage', 'currency']
				: ['currency', 'percentage']
		},

		currencyValue: {
			get() {
				return this.typingSide === 'currency' ? this.typingHeldValue : this.draftCurrency
			},
			set(value) {
				const parsed = this.parseModel(value)
				if (this.isDisplayRoundingEcho(parsed)) {
					return
				}
				this.draftCurrency = parsed
				this.draftPercentage = this.toPercentage(this.draftCurrency)
			},
		},

		percentageValue: {
			get() {
				return this.typingSide === 'percentage' ? this.typingHeldValue : this.draftPercentage
			},
			set(percent) {
				if (percent === this.draftPercentage) {
					return
				}
				this.draftPercentage = percent
				this.draftCurrency = this.toCurrency(percent)
			},
		},

		isFocused() {
			return this.focusedSide !== null
		},

		isCurrencyDisabled() {
			return this.disabled || this.disabledCurrency
		},

		isPercentageDisabled() {
			return this.disabled || this.disabledPercentage || !this.baseValue
		},

		isFullyDisabled() {
			return this.isCurrencyDisabled && this.isPercentageDisabled
		},

		currencyName() {
			return this.name ? `${this.name}_currency` : ''
		},

		percentageName() {
			return this.name ? `${this.name}_percentage` : ''
		},

		displayCurrencyCode() {
			return this.currency || this.userStore.userData.currency_code || 'EUR'
		},

		displayCurrencyValue() {
			return this.displayAsCurrency(
				this.draftCurrency ?? 0,
				this.currencyDigits,
				this.displayCurrencyCode,
			)
		},

		displayPercentageValue() {
			return this.displayAsPercent((this.draftPercentage ?? 0) / 100, this.percentageDigits)
		},

		isPercentageOverMax() {
			return this.draftPercentage != null && this.draftPercentage > this.maxPercent
		},
	},

	watch: {
		modelValue() {
			if (!this.isFocused) {
				this.syncDraftsFromModel()
			}
		},

		baseValue() {
			if (!this.isFocused) {
				this.syncDraftsFromModel()
			}
		},
	},

	created() {
		this.syncDraftsFromModel()
	},

	mounted() {
		if (this.validator) {
			if (this.modelValue === null || this.modelValue === '') {
				this.validator.$reset()
			} else {
				this.validator.$touch()
			}
		}

		if (this.isTableInput) {
			document.addEventListener('click', this.handleOutsideClick)
		}

		if (this.autofocus && !this.isTableInput) {
			this.$nextTick(() => this.applyAutofocus())
			// KEEP THIS COMMENT: The autofocus logic is a bit tricky
			// PrimeVue's BodyCell focuses the cell's FIRST focusable element
			// via setTimeout(1) when cell editing starts, overriding the
			// focus above. Re-assert it after that steal — but only while
			// focus is still inside this component (or nowhere), so a real
			// user focus change is never fought.
			setTimeout(() => {
				const active = document.activeElement
				if (this.$el?.contains?.(active) || active === document.body) {
					this.applyAutofocus()
				}
			}, 10)
		}
	},

	beforeUnmount() {
		if (this.isTableInput) {
			document.removeEventListener('click', this.handleOutsideClick)
		}
	},

	methods: {
		parseModel(value) {
			if (value === null || value === undefined || value === '') {
				return null
			}
			const parsed = parseFloat(value)
			return isNaN(parsed) ? null : parsed
		},

		roundTo(value, digits) {
			return Number(value.toFixed(digits))
		},

		toPercentage(amount) {
			if (amount === null) {
				return null
			}
			if (!this.baseValue) {
				return 0
			}
			return this.roundTo((100 * amount) / this.baseValue, this.percentageDigits)
		},

		toCurrency(percent) {
			if (percent === null) {
				return null
			}
			const value = roundHalfUp((this.baseValue * percent) / 100, this.currencyDigits)
			return percent <= 100 ? Math.min(value, this.baseValue) : value
		},

		isDisplayRoundingEcho(parsed) {
			return (
				parsed !== null &&
				this.draftCurrency !== null &&
				parsed !== this.draftCurrency &&
				parsed === roundHalfUp(this.draftCurrency, this.currencyDigits)
			)
		},

		syncDraftsFromModel() {
			this.typingSide = null
			this.draftCurrency = this.parseModel(this.modelValue)
			this.draftPercentage = this.toPercentage(this.draftCurrency)
		},

		onSideInput(side, value) {
			if (this.typingSide !== side) {
				this.typingHeldValue = side === 'currency' ? this.draftCurrency : this.draftPercentage
				this.typingSide = side
			}
			if (side === 'currency') {
				this.currencyValue = value
			} else {
				this.percentageValue = value
			}
		},

		commit() {
			this.typingSide = null
			const value = this.draftCurrency
			this.$emit('update:modelValue', value ? value.toString() : value)
			this.$emit('update:percentage', this.draftPercentage)

			if (this.validator) {
				this.validator.$touch()
			}
		},

		onSideFocus(side) {
			this.focusedSide = side
			this.$emit('focus')
		},

		onSideBlur(side) {
			if (this.focusedSide === side) {
				this.focusedSide = null
			}
			this.commit()
			this.$emit('blur')
		},

		handleEnter() {
			this.commit()
			if (this.isTableInput) {
				this.isEditing = false
			}
		},

		handleEscape() {
			if (!this.isTableInput) {
				return
			}
			this.syncDraftsFromModel()
			this.isEditing = false
		},

		handleTab(event) {
			const enabledSides = this.orderedSides.filter((side) => !this.isSideDisabled(side))
			if (enabledSides.length < 2) {
				return
			}

			if (!event.shiftKey && this.focusedSide === enabledSides[0]) {
				event.stopPropagation()
			} else if (event.shiftKey && this.focusedSide === enabledSides[1]) {
				event.stopPropagation()
			}
		},

		isSideDisabled(side) {
			return side === 'currency' ? this.isCurrencyDisabled : this.isPercentageDisabled
		},

		openEditor(side = null) {
			if (this.displayOnly || this.isFullyDisabled) {
				return
			}
			this.isEditing = true
			this.$nextTick(() => {
				if (side && !this.isSideDisabled(side)) {
					this.focusSide(side)
				} else {
					this.focusFirstSide()
				}
			})
		},

		applyAutofocus() {
			if (this.autofocusSide && !this.isSideDisabled(this.autofocusSide)) {
				this.focusSide(this.autofocusSide)
			} else {
				this.focusFirstSide()
			}
		},

		focusFirstSide() {
			const firstEnabledSide = this.orderedSides.find((side) => !this.isSideDisabled(side))
			if (firstEnabledSide) {
				this.focusSide(firstEnabledSide)
			}
		},

		focusSide(side) {
			const ref = side === 'currency' ? this.$refs.currencyInput : this.$refs.percentageInput
			const component = Array.isArray(ref) ? ref[0] : ref
			const el = component?.$el
			if (!el) {
				return
			}
			const input = el.querySelector?.('input') || el
			if (input?.focus) {
				input.focus()
				input.select?.()
			}
		},

		handleOutsideClick(event) {
			if (!this.isEditing) {
				return
			}
			const path = event.composedPath ? event.composedPath() : []
			if (!path.some((el) => el.id === this.dynamicId)) {
				this.commit()
				this.isEditing = false
			}
		},
	},
}
</script>

<style>
.core-currency-percentage-input {
	position: relative;
	width: 100%;

	& .core-currency-percentage-input__row {
		display: flex;
		align-items: center;
		gap: 4px;
		width: 100%;
	}

	& .core-currency-percentage-input__currency {
		flex: 0 1 14rem;
		min-width: 0;
	}

	& .core-currency-percentage-input__percentage {
		flex: 0 1 14rem;
		min-width: 0;
	}

	& .core-currency-percentage-input__swap-icon {
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		color: var(--color-secondary-200);
	}

	& .core-currency-percentage-input__display-box {
		font-size: var(--text-base);
		text-align: right;
		cursor: pointer;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	& .core-currency-percentage-input__display-box--disabled {
		background-color: var(--color-disabled-bg);
		color: var(--text-disabled);
		cursor: not-allowed;
	}

	& .core-currency-percentage-input__warning-icon {
		flex: 0 0 auto;
		display: inline-flex;
		align-items: center;
		color: var(--color-warning);
	}
}

/* Must follow the base block: same specificity, so source order decides. */
.core-currency-percentage-input--full-width {
	& .core-currency-percentage-input__currency,
	& .core-currency-percentage-input__percentage,
	& .core-currency-percentage-input__display-box {
		flex: 1 1 0;
	}
}
</style>
